import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronLeft, ChevronDown, FileText, GitBranch, Layers, Users,
  Cpu, Zap, Search, BarChart3, ArrowRight, CheckCircle2, Target,
  Workflow, Database, Shield, Map, TrendingUp, BookOpen, Settings,
  MessageSquare, Eye, RefreshCw, Brain
} from "lucide-react";
import LuminexExtractionCanvas from "../components/LuminexExtractionCanvas";

/* ───── CountUp ───── */
function CountUp({ end, prefix = "", suffix = "", duration = 1600, decimals = 0 }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting && !started.current) {
          started.current = true;
          const t0 = performance.now();
          const tick = (t) => {
            const p = Math.min(1, (t - t0) / duration);
            const eased = 1 - Math.pow(1 - p, 3);
            setVal(end * eased);
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      },
      { threshold: 0.3 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [end, duration]);
  const d = decimals ? val.toFixed(decimals) : Math.round(val).toLocaleString("en-IN");
  return <span ref={ref}>{prefix}{d}{suffix}</span>;
}

/* ───── FAQ Item ───── */
function FaqItem({ q, a, idx, openIdx, setOpenIdx }) {
  const isOpen = openIdx === idx;
  return (
    <div className={"vd-faq " + (isOpen ? "open" : "")} data-testid={`vd-faq-${idx}`}>
      <button className="vd-faq-q" onClick={() => setOpenIdx(isOpen ? -1 : idx)} type="button" data-testid={`vd-faq-toggle-${idx}`}>
        <span>{q}</span>
        <ChevronDown size={18} className={"vd-faq-chevron " + (isOpen ? "rotated" : "")} />
      </button>
      {isOpen && <div className="vd-faq-a" data-testid={`vd-faq-answer-${idx}`}>{a}</div>}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
/* SECTION 1 — HERO                                               */
/* ═══════════════════════════════════════════════════════════════ */
function Section1Hero() {
  return (
    <section className="vd-hero" data-testid="vd-hero">
      <div className="container vd-hero-split">
        <div className="vd-hero-copy">
          <Link to="/platforms" className="detail-back" data-testid="vd-back">
            <ChevronLeft size={14} /> All platforms
          </Link>
          <div className="vd-crumb mono" data-testid="vd-breadcrumb">Platforms / AI-Powered Consulting / LUMINEX</div>
          <div className="vd-eyebrow">BUSINESS PROCESS DESIGN &amp; RE-ENGINEERING</div>
          <h1 className="vd-h1" data-testid="vd-h1">
            <span className="vd-h1-indo vd-logo-lockup">
              <span className="vd-icon-diamond">
                <svg viewBox="0 0 56 56" width="56" height="56" aria-hidden="true">
                  <rect x="12" y="12" width="32" height="32" rx="3" className="vd-diamond-shape" fill="none" strokeWidth="2" transform="rotate(45 28 28)" />
                  <line x1="28" y1="0" x2="28" y2="10" className="vd-diamond-line" strokeWidth="1.5" strokeLinecap="round" />
                  <line x1="28" y1="46" x2="28" y2="56" className="vd-diamond-line" strokeWidth="1.5" strokeLinecap="round" />
                  <line x1="0" y1="28" x2="10" y2="28" className="vd-diamond-line" strokeWidth="1.5" strokeLinecap="round" />
                  <line x1="46" y1="28" x2="56" y2="28" className="vd-diamond-line" strokeWidth="1.5" strokeLinecap="round" />
                  <circle cx="28" cy="28" r="4" fill="#F26B1F" />
                </svg>
              </span>
              <span className="vd-wordmark">Luminex</span>
            </span>
            <span className="vd-h1-en">Map every process. Redesign what matters. In a fraction of the time.</span>
          </h1>
          <p className="vd-hero-sub" data-testid="vd-hero-sub">
            Luminex converts the invisible — SOPs, interviews, Slack threads, system logs, tribal knowledge — into BPMN 2.0 process maps, RACI matrices, and prioritized re-engineering roadmaps. Built for capital-project organizations where "how we actually work" is scattered across 17 systems and 40 years of institutional memory.
          </p>
          <div className="vd-hero-ctas">
            <a href="https://luminex.capexara.com/" target="_blank" rel="noopener noreferrer" className="btn btn-primary" data-testid="vd-cta-access">Access Luminex <span className="arrow">→</span></a>
            <Link to="/contact?p=luminex" className="btn btn-ghost" data-testid="vd-cta-demo">Request Demo <span className="arrow">→</span></Link>
            <button className="btn btn-ghost" type="button" onClick={() => document.getElementById("vd-canvas")?.scrollIntoView({ behavior: "smooth" })}>Explore the Canvas <span className="arrow">→</span></button>
          </div>
          <div className="vd-hero-trust mono" data-testid="vd-trust">
            BPMN 2.0 compliant · process-modeling and mining tools import · LLM-powered process mining · Delivered on Azure
          </div>
        </div>
        <div className="vd-hero-graphic" data-testid="vd-hero-graphic">
          <LuminexExtractionCanvas />
        </div>
      </div>
    </section>
  );
}

/* Animated BPMN Canvas — BP02.02.01.01 Initiate FEL-2 & Develop FEED Scope
   When `inline` is true, renders just the SVG without the outer wrap/header/meta
   (for embedding inside editor chrome like Section4Canvas). */
function BpmnCanvas({ inline = false }) {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const timers = [];
    for (let i = 1; i <= 10; i++) timers.push(setTimeout(() => setStep(i), 300 + i * 220));
    return () => timers.forEach(clearTimeout);
  }, []);

  const op = (n) => step >= n ? 1 : 0;
  const dash = (n) => step >= n ? "none" : "500";

  const svg = (
    <svg viewBox="0 0 920 420" className="vd-bpmn-svg" data-testid="vd-bpmn-svg" preserveAspectRatio="xMidYMid meet">
        <defs>
          <marker id="arrowhead" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
            <polygon points="0 0, 8 3, 0 6" className="vd-arrow-fill" />
          </marker>
        </defs>
        {/* Swimlane backgrounds — 3 lanes: Sponsor / Eng Lead / Procurement */}
        <rect x="56" y="0" width="864" height="110" className="vd-lane vd-lane-1" />
        <rect x="56" y="110" width="864" height="150" className="vd-lane vd-lane-2" />
        <rect x="56" y="260" width="864" height="110" className="vd-lane vd-lane-1" />
        {/* Lane dividers */}
        <line x1="56" y1="110" x2="920" y2="110" className="vd-lane-div" />
        <line x1="56" y1="260" x2="920" y2="260" className="vd-lane-div" />
        {/* Lane sidebar labels */}
        <rect x="0" y="0" width="56" height="110" className="vd-lane-sidebar" />
        <rect x="0" y="110" width="56" height="150" className="vd-lane-sidebar-alt" />
        <rect x="0" y="260" width="56" height="110" className="vd-lane-sidebar" />
        <text x="28" y="55" textAnchor="middle" className="vd-bpmn-lane-label" transform="rotate(-90 28 55)">A01 Project Sponsor</text>
        <text x="28" y="185" textAnchor="middle" className="vd-bpmn-lane-label" transform="rotate(-90 28 185)">A05 Engineering Lead</text>
        <text x="28" y="315" textAnchor="middle" className="vd-bpmn-lane-label" transform="rotate(-90 28 315)">A08 Procurement</text>

        {/* ── SPONSOR LANE ─────────────────────────────── */}
        {/* Step 1: Start event */}
        <g opacity={op(1)}>
          <circle cx="88" cy="55" r="11" fill="none" className="vd-el-stroke" strokeWidth="2.5" />
          <line x1="99" y1="55" x2="128" y2="55" className="vd-el-stroke" strokeWidth="1.5" markerEnd="url(#arrowhead)" strokeDasharray={dash(1)} />
        </g>
        {/* Step 2: Initiate FEL-2 task */}
        <g opacity={op(2)}>
          <rect x="128" y="30" width="140" height="50" rx="6" className="vd-task" />
          <text x="198" y="52" textAnchor="middle" className="vd-bpmn-task-text">Initiate FEL-2</text>
          <text x="198" y="66" textAnchor="middle" className="vd-bpmn-task-text-sm">(Conceptual Engineering)</text>
        </g>
        {/* Arrow to Gateway 1 */}
        <line x1="268" y1="55" x2="296" y2="55" className="vd-el-stroke" strokeWidth="1.5" markerEnd="url(#arrowhead)" strokeDasharray={dash(3)} opacity={op(3)} />
        {/* Step 3: Gateway 1 — Sponsor decision */}
        <g opacity={op(3)}>
          <polygon points="320,35 345,55 320,75 295,55" className="vd-gateway" />
          <text x="320" y="59" textAnchor="middle" className="vd-bpmn-gateway-text">+</text>
        </g>
        {/* Gateway 1 down-arrow → Engineering Lane (Develop FEED Scope) */}
        <line x1="320" y1="75" x2="320" y2="145" className="vd-el-stroke" strokeWidth="1.5" markerEnd="url(#arrowhead)" strokeDasharray={dash(4)} opacity={op(4)} />

        {/* ── ENGINEERING LEAD LANE ────────────────────── */}
        {/* Step 4: Develop FEED Scope */}
        <g opacity={op(4)}>
          <rect x="250" y="145" width="140" height="50" rx="6" className="vd-task" />
          <text x="320" y="167" textAnchor="middle" className="vd-bpmn-task-text">Develop FEED</text>
          <text x="320" y="181" textAnchor="middle" className="vd-bpmn-task-text-sm">Scope</text>
          {/* AI annotation */}
          <rect x="250" y="205" width="140" height="22" rx="5" className="vd-ai-badge" />
          <circle cx="264" cy="216" r="5" className="vd-ai-dot" />
          <text x="274" y="220" className="vd-bpmn-ai-text">AI: 14-day avg. Benchmark: 9d</text>
        </g>
        {/* Arrow to Gateway 2 */}
        <line x1="390" y1="170" x2="418" y2="170" className="vd-el-stroke" strokeWidth="1.5" markerEnd="url(#arrowhead)" strokeDasharray={dash(5)} opacity={op(5)} />
        {/* Step 5: Gateway 2 — parallel split */}
        <g opacity={op(5)}>
          <polygon points="442,150 467,170 442,190 417,170" className="vd-gateway" />
          <text x="442" y="174" textAnchor="middle" className="vd-bpmn-gateway-text">+</text>
        </g>
        {/* Split up to Issue RFQ and down to Draft TORs */}
        <path d="M 467 170 L 480 170 L 480 138 L 500 138" className="vd-el-stroke" fill="none" strokeWidth="1.5" markerEnd="url(#arrowhead)" strokeDasharray={dash(6)} opacity={op(6)} />
        <path d="M 467 170 L 480 170 L 480 202 L 500 202" className="vd-el-stroke" fill="none" strokeWidth="1.5" markerEnd="url(#arrowhead)" strokeDasharray={dash(6)} opacity={op(6)} />
        {/* Step 6: Issue RFQ (top) & Draft TORs (bottom) */}
        <g opacity={op(6)}>
          <rect x="500" y="118" width="130" height="40" rx="6" className="vd-task" />
          <text x="565" y="143" textAnchor="middle" className="vd-bpmn-task-text">Issue RFQ</text>
          <rect x="500" y="182" width="130" height="40" rx="6" className="vd-task" />
          <text x="565" y="207" textAnchor="middle" className="vd-bpmn-task-text">Draft TORs</text>
        </g>
        {/* Join arrows to Gateway 3 */}
        <path d="M 630 138 L 650 138 L 650 170 L 663 170" className="vd-el-stroke" fill="none" strokeWidth="1.5" markerEnd="url(#arrowhead)" strokeDasharray={dash(7)} opacity={op(7)} />
        <path d="M 630 202 L 650 202 L 650 170 L 663 170" className="vd-el-stroke" fill="none" strokeWidth="1.5" markerEnd="url(#arrowhead)" strokeDasharray={dash(7)} opacity={op(7)} />
        {/* Step 7: Gateway 3 — parallel join */}
        <g opacity={op(7)}>
          <polygon points="688,150 713,170 688,190 663,170" className="vd-gateway" />
          <text x="688" y="174" textAnchor="middle" className="vd-bpmn-gateway-text">+</text>
          {/* AI annotation for TOR risk */}
          <rect x="618" y="225" width="160" height="22" rx="5" className="vd-ai-badge vd-ai-warn" />
          <circle cx="632" cy="236" r="5" className="vd-ai-dot vd-ai-dot-warn" />
          <text x="642" y="240" className="vd-bpmn-ai-text">AI: 34% skip TOR. Gov. risk.</text>
        </g>

        {/* ── PROCUREMENT LANE ─────────────────────────── */}
        {/* Gateway 2 down-arrow to Procurement (Budget Estimate trigger) */}
        <path d="M 442 190 L 442 230 L 500 230 L 500 300 L 565 300" className="vd-el-stroke" fill="none" strokeWidth="1.5" strokeDasharray="4 3" opacity={op(8) * 0.6} />
        {/* Step 8: Budget Estimate */}
        <g opacity={op(8)}>
          <rect x="500" y="285" width="140" height="40" rx="6" className="vd-task" />
          <text x="570" y="310" textAnchor="middle" className="vd-bpmn-task-text">Budget Estimate</text>
          {/* Owner annotation */}
          <line x1="640" y1="305" x2="680" y2="305" className="vd-el-stroke" strokeWidth="1" strokeDasharray="2 2" />
          <text x="684" y="309" className="vd-bpmn-owner-text">Harshil</text>
        </g>

        {/* ── BACK TO SPONSOR: Review & Approve ───────── */}
        {/* Join arrow Gateway 3 → up to sponsor lane → Review & Approve */}
        <path d="M 713 170 L 740 170 L 740 55 L 758 55" className="vd-el-stroke" fill="none" strokeWidth="1.5" markerEnd="url(#arrowhead)" strokeDasharray={dash(9)} opacity={op(9)} />
        {/* Step 9: Review & Approve */}
        <g opacity={op(9)}>
          <rect x="758" y="30" width="130" height="50" rx="6" className="vd-task" />
          <text x="823" y="52" textAnchor="middle" className="vd-bpmn-task-text">Review &amp; Approve</text>
          <text x="823" y="66" textAnchor="middle" className="vd-bpmn-task-text-sm">(FEED scope + budget)</text>
          {/* AI annotation */}
          <rect x="738" y="88" width="170" height="22" rx="5" className="vd-ai-badge" />
          <circle cx="752" cy="99" r="5" className="vd-ai-dot" />
          <text x="762" y="103" className="vd-bpmn-ai-text">AI: 6d wait. Escalation opp.</text>
        </g>

        {/* Step 10: End event */}
        <g opacity={op(10)}>
          <line x1="888" y1="55" x2="902" y2="55" className="vd-el-stroke" strokeWidth="1.5" strokeDasharray={dash(10)} />
          <circle cx="904" cy="55" r="11" fill="none" className="vd-el-stroke" strokeWidth="3" />
        </g>

        {/* Cross-Process Interfaces */}
        <g opacity={op(10)}>
          <line x1="56" y1="380" x2="920" y2="380" className="vd-lane-div" strokeDasharray="4 4" />
          <text x="66" y="398" className="vd-bpmn-xref-label">CROSS-PROCESS INTERFACES</text>
          <rect x="240" y="386" width="160" height="18" rx="4" className="vd-xref-chip" />
          <text x="320" y="399" textAnchor="middle" className="vd-bpmn-xref-text">BP02.01.05 FEL-1 Exit</text>
          <rect x="420" y="386" width="160" height="18" rx="4" className="vd-xref-chip" />
          <text x="500" y="399" textAnchor="middle" className="vd-bpmn-xref-text">BP02.03.01 FEL-3 Entry</text>
          <rect x="600" y="386" width="180" height="18" rx="4" className="vd-xref-chip" />
          <text x="690" y="399" textAnchor="middle" className="vd-bpmn-xref-text">BP05.02.01 Vendor Onboarding</text>
        </g>
      </svg>
  );

  if (inline) return svg;

  return (
    <div className="vd-bpmn-wrap">
      <div className="vd-bpmn-header">
        <div className="vd-bpmn-id">BP02.02.01.01</div>
        <div className="vd-bpmn-title-bar">Initiate FEL-2 and develop FEED scope with RFQ, Terms of Reference and budget estimate</div>
      </div>
      <div className="vd-bpmn-meta-row">
        <span>D02 &mdash; Front-End Loading</span>
        <span>F02.02 &mdash; FEL-2 (Conceptual Engineering)</span>
        <span>Owner: Project Sponsor &amp; Engineering Lead</span>
        <span>Output: Approved FEED Scope &amp; Budget</span>
      </div>
      {svg}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
/* SECTION 2 — THE PROBLEM                                        */
/* ═══════════════════════════════════════════════════════════════ */
function Section2Problem() {
  return (
    <section className="vd-problem" data-testid="vd-problem">
      <div className="container">
        <div className="vd-eyebrow" style={{ color: "rgba(243,244,246,0.55)" }}>THE PROCESS KNOWLEDGE CRISIS</div>
        <h2 className="vd-problem-h2">
          Your enterprise already knows how it works. It just can't find the knowledge, consolidate it, or redesign it fast enough.
        </h2>
        <div className="vd-problem-stats" data-testid="vd-problem-stats">
          <div className="vd-problem-stat">
            <div className="vd-problem-stat-num"><CountUp end={26} suffix=" weeks" /></div>
            <div className="vd-problem-stat-desc">Average time for a top-tier consulting-firm BPM engagement on a mid-sized enterprise</div>
          </div>
          <div className="vd-problem-stat">
            <div className="vd-problem-stat-num"><CountUp end={8} prefix="₹4–" suffix=" Cr" /></div>
            <div className="vd-problem-stat-desc">Typical spend for a single BPM engagement (consulting + software)</div>
          </div>
          <div className="vd-problem-stat">
            <div className="vd-problem-stat-num"><CountUp end={60} suffix="%" /></div>
            <div className="vd-problem-stat-desc">Of process documentation is outdated within 18 months of delivery</div>
          </div>
          <div className="vd-problem-stat">
            <div className="vd-problem-stat-num"><CountUp end={200} suffix="+" /></div>
            <div className="vd-problem-stat-desc">Processes in a mid-sized EPC lifecycle — each with 3–5 regional variants</div>
          </div>
        </div>
        <p className="vd-problem-manifesto">"The most expensive process map is the one nobody trusts."</p>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
/* SECTION 3 — THE METHOD (5 PHASES)                              */
/* ═══════════════════════════════════════════════════════════════ */
const PHASES = [
  { n: 1, title: "INGEST", time: "Week 1", desc: "The cognitive intake layer. Upload SOPs, paste interview transcripts, connect to document libraries/document libraries, stream team chat tools exports, or drop system logs.", acts: ["Document parsers (PDF, Word, Excel, Visio, MPP)", "Interview transcript analyzer (multi-speaker, multi-language)", "System log ingestion (enterprise ERP, legacy schedulers, scheduling tools, Slack)"] },
  { n: 2, title: "DISCOVER", time: "Week 2", desc: "AI-powered process discovery. LLM + graph-reasoning engine extracts activities, actors, artifacts, and sequences from unstructured inputs.", acts: ["Activity & actor extraction", "Variant detection (actual vs documented)", "Tribal-knowledge capture via guided interview scripts"] },
  { n: 3, title: "MODEL", time: "Weeks 2–3", desc: "Auto-generates BPMN 2.0 swimlane diagrams. Editable on the Luminex Canvas — a full-featured BPMN editor with AI co-pilot.", acts: ["BPMN 2.0 compliant (lanes, pools, events, gateways, tasks)", "AI co-pilot suggests missing paths and error boundaries", "Collaborative multi-user editing (CRDT-based)"] },
  { n: 4, title: "ASSESS", time: "Week 4", desc: "Gap-to-best-practice analysis. Benchmarks against CII-BPM, APQC-PCF, CURT, and industry-specific standards.", acts: ["RACI matrix auto-generation from BPMN model", "Systems-of-record mapping", "Heatmap of gaps by severity, cost, and time-to-value"] },
  { n: 5, title: "RE-ENGINEER", time: "Weeks 5–6", desc: "Prioritized to-be design with ROI modeling.", acts: ["Side-by-side as-is vs to-be canvas", "ROI calculator per re-engineered process", "Change-impact analysis across systems and roles"] },
];

function Section3Method() {
  return (
    <section className="vd-method" data-testid="vd-method">
      <div className="container">
        <div className="vd-eyebrow">THE METHOD</div>
        <h2 className="vd-section-h2">Five phases. Six weeks. One canonical process atlas.</h2>
        <div className="vd-phases" data-testid="vd-phases">
          {PHASES.map((p) => (
            <div className="vd-phase" key={p.n} data-testid={`vd-phase-${p.n}`}>
              <div className="vd-phase-head">
                <span className="vd-phase-num">0{p.n}</span>
                <span className="vd-phase-title">{p.title}</span>
                <span className="vd-phase-time">{p.time}</span>
              </div>
              <p className="vd-phase-desc">{p.desc}</p>
              <ul className="vd-phase-acts">
                {p.acts.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            </div>
          ))}
        </div>
        <div className="vd-method-summary">
          From first upload to final roadmap — <strong>6 weeks</strong>. Re-engineering that used to take 6 months, done right.
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
/* SECTION 4 — THE CANVAS (DEEP-DIVE)                            */
/* ═══════════════════════════════════════════════════════════════ */
const CANVAS_FEATURES = [
  { icon: FileText, title: "BPMN 2.0 native", desc: "Full compliance. Import from process-modeling and diagramming tools. Export to all standard formats." },
  { icon: Brain, title: "AI co-pilot", desc: "Suggests missing paths, error handling, compensations, and optimization opportunities. Based on 500+ reference processes." },
  { icon: Users, title: "Real-time collaboration", desc: "CRDT-based multi-user editing. See cursors, comments, and edits live. Version history with semantic diffs." },
  { icon: Layers, title: "Auto-generated RACI", desc: "Draw the process, get the RACI matrix. Draw the RACI, get the process. Bidirectional sync." },
  { icon: Database, title: "Systems mapping", desc: "Tag each activity with the system it runs in. Auto-generates systems-of-record heatmaps." },
  { icon: Eye, title: "Variant management", desc: "Capture regional, BU, or project-stage variants. Compare side-by-side; identify and standardize best-in-class." },
  { icon: BarChart3, title: "Simulation & bottleneck analysis", desc: "Run 1,000 instances. Identify bottlenecks, wait-time accumulation, resource conflicts. Output optimization recs." },
];

function Section4Canvas() {
  return (
    <section className="vd-canvas" id="vd-canvas" data-testid="vd-canvas">
      <div className="container vd-canvas-split">
        <div className="vd-canvas-mockup" data-testid="vd-canvas-mockup">
          <div className="vd-mock-toolbar">
            <span className="vd-mock-logo">V</span>
            <span>File</span><span>Edit</span><span>View</span><span>Insert</span>
            <span className="vd-mock-ai-btn">AI Assist</span>
            <span>Collaborate</span>
          </div>
          <div className="vd-mock-body">
            <div className="vd-mock-sidebar">
              <div className="vd-mock-sb-item">Tasks</div>
              <div className="vd-mock-sb-item">Events</div>
              <div className="vd-mock-sb-item">Gateways</div>
              <div className="vd-mock-sb-item">Data</div>
              <div className="vd-mock-sb-item">Lanes</div>
            </div>
            <div className="vd-mock-canvas-area">
              <BpmnCanvas inline />
            </div>
            <div className="vd-mock-props">
              <div className="vd-mock-prop-title">Properties</div>
              <div className="vd-mock-prop-row"><span>Task:</span> Issue RFQ</div>
              <div className="vd-mock-prop-row"><span>Owner:</span> Engineering Lead</div>
              <div className="vd-mock-prop-row"><span>SLA:</span> 5 days</div>
              <div className="vd-mock-prop-row"><span>System:</span> enterprise procurement suites</div>
              <div className="vd-mock-prop-ai">AI: 34% of flows skip TOR drafting — governance risk</div>
            </div>
          </div>
          <div className="vd-mock-bottom">
            <span>Zoom: 80%</span><span>v2.4</span><span>3 collaborators</span>
          </div>
        </div>
        <div className="vd-canvas-copy">
          <h2 className="vd-section-h2">The canvas that designs with you.</h2>
          <div className="vd-canvas-features" data-testid="vd-canvas-features">
            {CANVAS_FEATURES.map((f, i) => (
              <div className="vd-canvas-feat" key={i} data-testid={`vd-canvas-feat-${i}`}>
                <f.icon size={18} className="vd-canvas-feat-icon" />
                <div>
                  <div className="vd-canvas-feat-title">{f.title}</div>
                  <div className="vd-canvas-feat-desc">{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
          <p className="vd-pull-quote">"Process design is the architecture of decisions. Luminex makes it legible."</p>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
/* SECTION 5 — THE AI ENGINE                                      */
/* ═══════════════════════════════════════════════════════════════ */
const AI_CAPS = [
  { label: "Extract", bubbles: ["PBS node mismatch detected", "12 actors identified"] },
  { label: "Detect", bubbles: ["Variant: 12 regional flavors", "As-doc ≠ as-executed"] },
  { label: "Benchmark", bubbles: ["Cycle time > bench by 38%", "CII-BPM v4 reference"] },
  { label: "Redesign", bubbles: ["Proposed: parallel chain", "ROI: ₹2.4 Cr / yr"] },
  { label: "Query", bubbles: ["Why 14-day FEED delay?", "Root cause: 3 approvals"] },
];

function Section5AIEngine() {
  const [activeAgent, setActiveAgent] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => setActiveAgent((a) => (a + 1) % 5), 3000);
    return () => clearInterval(iv);
  }, []);

  return (
    <section className="vd-ai" data-testid="vd-ai-engine">
      <div className="container vd-ai-split">
        <div className="vd-ai-copy">
          <div className="vd-eyebrow" style={{ color: "rgba(243,244,246,0.55)" }}>THE COGNITIVE LAYER</div>
          <h2 className="vd-section-h2" style={{ color: "#fff" }}>Not a chatbot. A reasoning engine for processes.</h2>
          <p className="vd-ai-desc">
            Luminex's cognitive layer is a purpose-built multi-agent system trained on 500+ reference processes across EPC, Oil &amp; Gas, Power, Chemicals, and Infrastructure. It doesn't just answer questions — it reasons about your process structure, finds gaps, and proposes redesigns grounded in industry best practices.
          </p>
          <div className="vd-ai-caps">
            {["Process extraction", "Variant detection", "Gap analysis", "Re-engineering proposals", "Natural-language queries"].map((c, i) => (
              <div className={"vd-ai-cap " + (activeAgent === i ? "active" : "")} key={i}>
                <Zap size={14} /> <span>{c}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="vd-ai-graph" data-testid="vd-ai-graph">
          <svg viewBox="0 0 400 400" className="vd-ai-svg">
            {/* Center */}
            <polygon points="200,160 230,177 230,210 200,227 170,210 170,177" fill="rgba(30,78,140,0.15)" stroke="#1E4E8C" strokeWidth="1.5" />
            <text x="200" y="198" textAnchor="middle" fontSize="10" fill="#6ac8ff" fontFamily="var(--f-mono)">PROCESS</text>
            {/* Agent nodes */}
            {AI_CAPS.map((agent, i) => {
              const angle = (i * 72 - 90) * (Math.PI / 180);
              const r = 130;
              const x = 200 + r * Math.cos(angle);
              const y = 193 + r * Math.sin(angle);
              const isActive = activeAgent === i;
              return (
                <g key={i}>
                  <line x1="200" y1="193" x2={x} y2={y} stroke={isActive ? "#F26B1F" : "rgba(30,78,140,0.3)"} strokeWidth={isActive ? 1.5 : 0.8} />
                  <circle cx={x} cy={y} r={isActive ? 22 : 18} fill={isActive ? "rgba(242,107,31,0.12)" : "rgba(30,78,140,0.08)"} stroke={isActive ? "#F26B1F" : "#1E4E8C"} strokeWidth={isActive ? 1.5 : 1} />
                  <text x={x} y={y + 3} textAnchor="middle" fontSize="8" fill={isActive ? "#F26B1F" : "#9AA2B1"} fontFamily="var(--f-mono)">{agent.label}</text>
                  {/* Thought bubbles */}
                  {isActive && agent.bubbles.map((b, j) => {
                    const bx = x + (j === 0 ? -40 : 40);
                    const by = y + (j === 0 ? -30 : 30);
                    return (
                      <g key={j} className="vd-ai-bubble">
                        <rect x={bx - 55} y={by - 8} width="110" height="16" rx="3" fill="rgba(10,14,26,0.8)" stroke="rgba(242,107,31,0.25)" strokeWidth="0.5" />
                        <text x={bx} y={by + 3} textAnchor="middle" fontSize="6" fill="rgba(243,244,246,0.7)" fontFamily="var(--f-mono)">{b}</text>
                      </g>
                    );
                  })}
                </g>
              );
            })}
          </svg>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
/* SECTION 6 — DELIVERABLES                                       */
/* ═══════════════════════════════════════════════════════════════ */
const DELIVERABLES = [
  { icon: Map, title: "As-Is Process Atlas", desc: "Complete BPMN 2.0 repository of every in-scope process, with swimlanes, variants, and metadata." },
  { icon: RefreshCw, title: "To-Be Process Atlas", desc: "Re-engineered process designs with clear rationale, system impacts, and expected outcomes." },
  { icon: Users, title: "RACI Matrix", desc: "Role-activity ownership matrix, auto-generated from the BPMN model, validated by stakeholders." },
  { icon: Database, title: "Systems-of-Record Map", desc: "Which system holds which data, which activity touches which system, integration dependencies." },
  { icon: Target, title: "Gap Heatmap", desc: "Visual gap analysis scored on 12 dimensions, with severity, effort, and ROI ratings." },
  { icon: Shield, title: "Control-Point Register", desc: "SOX, regulatory, and risk control points embedded in each process, with evidence linkages." },
  { icon: TrendingUp, title: "Re-Engineering Roadmap", desc: "Prioritized implementation plan with quick-wins, structural changes, and change management." },
  { icon: BarChart3, title: "ROI Model", desc: "Financial impact of re-engineering per process, consolidated to enterprise-level business case." },
  { icon: Workflow, title: "Maturity Baseline", desc: "Starting maturity score per process area; integrates with Assayo (Maturity Assessment)." },
];

function Section6Deliverables() {
  return (
    <section className="vd-deliverables" data-testid="vd-deliverables">
      <div className="container">
        <div className="vd-eyebrow">YOUR PROCESS ATLAS</div>
        <h2 className="vd-section-h2">Nine deliverables. One source of truth for how you work.</h2>
        <div className="vd-deliv-grid" data-testid="vd-deliv-grid">
          {DELIVERABLES.map((d, i) => (
            <div className="vd-deliv-card" key={i} data-testid={`vd-deliv-${i}`}>
              <d.icon size={22} className="vd-deliv-icon" />
              <h3 className="vd-deliv-title">{d.title}</h3>
              <p className="vd-deliv-desc">{d.desc}</p>
            </div>
          ))}
        </div>
        <p className="vd-deliv-sister">
          Luminex outputs feed directly into <Link to="/platforms/assayo">Assayo</Link> (Maturity Assessment), <Link to="/platforms/evalore">Evalore</Link> (Platform Evaluation), and <Link to="/platforms/cognara">Cognara</Link> (GenAI Agent) — creating a compounding intelligence layer across Capexara's consulting platforms.
        </p>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
/* SECTION 7 — INDUSTRIES & USE CASES                             */
/* ═══════════════════════════════════════════════════════════════ */
const USE_CASES = [
  { title: "Capital Project Lifecycle Re-engineering", desc: "Map and redesign end-to-end EPC lifecycle — from FEL-1 scoping through commissioning. Typical: 120+ processes, 6-week delivery." },
  { title: "Digital Transformation Readiness", desc: "Assess process maturity before a ₹200 Cr ERP or PLM rollout. Identify what to fix before, during, and after implementation." },
  { title: "M&A Post-Merger Integration", desc: "Map both entities' processes; identify overlaps, gaps, and harmonization opportunities. Accelerate integration by 40%." },
  { title: "Regulatory Compliance Mapping", desc: "Map processes to SOX, SEBI, CPCB, BOCW, GST requirements. Identify control gaps and automate compliance capture." },
  { title: "Operational Excellence Initiatives", desc: "Target specific high-pain processes (RFQ cycle, claims, change orders). Deliver focused re-engineering in 4 weeks." },
];

const VD_INDUSTRIES = [
  "Oil, Gas & Energy", "Power & Utilities", "Chemicals", "Infrastructure",
  "Metals, Mining & Minerals", "Manufacturing", "Marine",
];

function Section7Industries() {
  return (
    <section className="vd-industries" data-testid="vd-industries">
      <div className="container vd-ind-split">
        <div className="vd-ind-left">
          <div className="vd-eyebrow">INDUSTRY FIT</div>
          <h2 className="vd-section-h2">Deployable across all 7 capital-project industries.</h2>
          <div className="vd-ind-chips" data-testid="vd-ind-chips">
            {VD_INDUSTRIES.map((ind, i) => (
              <span className="vd-ind-chip" key={i}>{ind}</span>
            ))}
          </div>
        </div>
        <div className="vd-ind-right">
          <div className="vd-eyebrow">USE CASES</div>
          <div className="vd-usecases" data-testid="vd-usecases">
            {USE_CASES.map((uc, i) => (
              <div className="vd-usecase" key={i} data-testid={`vd-usecase-${i}`}>
                <h3>{uc.title}</h3>
                <p>{uc.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
/* SECTION 8 — OUTCOMES & ROI                                     */
/* ═══════════════════════════════════════════════════════════════ */
function Section8Outcomes() {
  return (
    <section className="vd-outcomes" data-testid="vd-outcomes">
      <div className="container">
        <div className="vd-eyebrow">MEASURED IMPACT</div>
        <h2 className="vd-section-h2">What enterprises measure after deploying Luminex.</h2>
        <div className="vd-outcome-cards" data-testid="vd-outcome-cards">
          <div className="vd-outcome-card">
            <div className="vd-outcome-num"><CountUp end={6} suffix=" weeks" /></div>
            <div className="vd-outcome-label">End-to-end delivery vs 26 weeks for traditional consulting</div>
            <div className="vd-outcome-bar"><div className="vd-outcome-fill" style={{ width: "23%" }} /><span>75% time savings</span></div>
          </div>
          <div className="vd-outcome-card">
            <div className="vd-outcome-num"><CountUp end={65} suffix="%" /></div>
            <div className="vd-outcome-label">Reduction in process design cost vs traditional BPM consulting</div>
            <div className="vd-outcome-bar"><div className="vd-outcome-fill" style={{ width: "65%" }} /><span>From ₹4-8 Cr to ₹1.2-2.5 Cr</span></div>
          </div>
          <div className="vd-outcome-card">
            <div className="vd-outcome-num"><CountUp end={3.1} suffix="×" decimals={1} /></div>
            <div className="vd-outcome-label">Faster decision-making after re-engineering</div>
            <div className="vd-outcome-bar"><div className="vd-outcome-fill" style={{ width: "75%" }} /><span>Measured across 14 deployments</span></div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
/* SECTION 9 — FAQ                                                */
/* ═══════════════════════════════════════════════════════════════ */
const FAQS = [
  { q: "How is Luminex different from process modeling and mining tools?", a: "Process modeling tools require consultants to do the actual modeling, gap analysis, and redesign. Process mining shows what's happening but doesn't redesign. Luminex is end-to-end: AI does the modeling, the gap analysis, and proposes the redesign. You still need humans for judgment calls and change management, but the heavy lifting is automated." },
  { q: "Can Luminex import from our existing BPM tools?", a: "Yes. Luminex imports from leading BPM tools and raw BPMN 2.0 XML. You don't lose your existing process library — you enrich it with AI analysis." },
  { q: "What reference models does Luminex benchmark against?", a: "APQC Process Classification Framework (PCF), CII Best Practices for Capital Projects, CURT Tripartite Initiative, ISO 9001 process families, industry-specific models (IOGP for O&G, IEEE for Power), and 500+ real-world reference processes from Capexara's consulting engagements." },
  { q: "How does the AI handle sensitive process documentation?", a: "Luminex deploys in three modes: (1) Shared cloud on Azure with customer-specific tenant isolation, (2) Dedicated cloud with full data residency in India, (3) On-premise for defense, nuclear, and PSU clients. All AI inference can be pointed to self-hosted LLMs for maximum data control." },
  { q: "Can we use Luminex for process mining (analyzing system logs)?", a: "Partially. Luminex integrates with process-mining platforms for deep log analysis. Luminex's own capability is focused on lightweight log ingestion (enterprise ERP, legacy schedulers, scheduling tools, enterprise workflow platforms) to validate documented processes against actual execution." },
  { q: "What's the typical engagement size?", a: "Three models: (1) Software-only subscription (₹24-60 Lakh/year depending on seats and scope), (2) Assisted engagement with 2-3 Capexara consultants + software (₹1.2-2.5 Cr for 6-week program), (3) Full managed transformation (₹3-8 Cr for enterprise-wide re-engineering program with change management)." },
  { q: "How do you ensure process designs are actually adopted?", a: "Luminex is the design layer, not the execution layer. Adoption requires change management, training, and process execution tooling. We partner with enterprise workflow platforms (workflow execution platforms) for execution. Our to-be designs are exportable as executable BPMN to these platforms." },
  { q: "Is Luminex only for large enterprises?", a: "No. Luminex scales from a single high-pain process (4-week engagement, ₹40-80L) to enterprise-wide transformation. The methodology and platform are the same; the scope adjusts." },
];

function Section9FAQ() {
  const [openIdx, setOpenIdx] = useState(-1);
  return (
    <section className="vd-faq-section" data-testid="vd-faq">
      <div className="container">
        <div className="vd-eyebrow">FAQ</div>
        <h2 className="vd-section-h2">Frequently asked about Luminex.</h2>
        <div className="vd-faq-list" data-testid="vd-faq-list">
          {FAQS.map((f, i) => (
            <FaqItem key={i} q={f.q} a={f.a} idx={i} openIdx={openIdx} setOpenIdx={setOpenIdx} />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
/* SECTION 10 — CTA FOOTER                                        */
/* ═══════════════════════════════════════════════════════════════ */
function Section10CTA() {
  return (
    <section className="vd-cta" data-testid="vd-cta">
      <div className="container" style={{ textAlign: "center" }}>
        <div className="vd-cta-eyebrow">START WITH ONE PROCESS</div>
        <h2 className="vd-cta-h2">
          Pick your most painful process.<br />We'll map it, benchmark it, and redesign it — in 4 weeks.
        </h2>
        <p className="vd-cta-sub">
          Our "One Process Pilot" is a fixed-scope, fixed-price engagement. Bring your worst process — RFQ cycle, change orders, claims, whatever keeps your COO up at night. We'll deliver a complete as-is map, gap analysis, and to-be design. If you're not impressed, you don't pay the final milestone.
        </p>
        <div className="vd-cta-btns">
          <Link to="/contact?p=luminex" className="btn btn-primary btn-lg" data-testid="vd-cta-pilot">Start the One-Process Pilot <span className="arrow">→</span></Link>
          <Link to="/contact?p=luminex" className="btn btn-ghost btn-lg" data-testid="vd-cta-demo-bottom">Book a 30-min platform demo</Link>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
/* PAGE EXPORT                                                    */
/* ═══════════════════════════════════════════════════════════════ */
export default function PlatformVidhana() {
  return (
    <>
      <Section1Hero />
      <Section2Problem />
      <Section3Method />
      <Section4Canvas />
      <Section5AIEngine />
      <Section6Deliverables />
      <Section7Industries />
      <Section8Outcomes />
      <Section9FAQ />
      <Section10CTA />
    </>
  );
}
